import { useState } from "react";

import {
  browseProjectData,
  projectDataExportUrl,
  restoreProjectData,
  type AppDataRecord,
} from "../../lib/account-client";

export interface ProjectDataBrowserApi {
  browseProjectData: typeof browseProjectData;
  restoreProjectData: typeof restoreProjectData;
}

export function ProjectDataBrowser({ workspaceId, projectId, api }: {
  workspaceId: string;
  projectId: string;
  api: ProjectDataBrowserApi;
}) {
  const [records, setRecords] = useState<AppDataRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const load = async () => {
    setWorking(true);
    setError(null);
    try { setRecords(await api.browseProjectData(workspaceId, projectId)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Doorway could not load this app's data."); }
    finally { setWorking(false); }
  };
  const restore = async (file: File) => {
    setWorking(true);
    setError(null);
    try {
      const backup = JSON.parse(await file.text()) as unknown;
      await api.restoreProjectData(workspaceId, projectId, backup);
      setRecords(await api.browseProjectData(workspaceId, projectId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not restore this data export.");
    } finally { setWorking(false); }
  };
  return <details className="project-data-browser" onToggle={(event) => { if (event.currentTarget.open && records === null && !working) void load(); }}><summary>Saved app data</summary><div className="project-data-actions"><a className="text-button" href={projectDataExportUrl(workspaceId, projectId)}>Export JSON</a><label className="text-button">Restore JSON<input type="file" accept="application/json,.json" disabled={working} onChange={(event) => { const file = event.target.files?.[0]; if (file) void restore(file); event.currentTarget.value = ""; }} /></label></div>{error && <p className="error-message" role="alert">{error}</p>}{working && <p>Loading saved items…</p>}{records && (records.length === 0 ? <p>No saved items yet. Apps can use the collection API at <code>/.mph/data/v1/collections/name</code>.</p> : <div className="data-record-list">{records.map((record) => <article key={`${record.collection}:${record.id}`}><span>{record.collection}</span><strong>{record.id}</strong><pre>{JSON.stringify(record.data, null, 2)}</pre></article>)}</div>)}</details>;
}
